import { useRef, ChangeEvent, useReducer, useState } from 'react'
import { t } from '@lingui/core/macro'
import { Visibility } from '@mui/icons-material'
import { Button, Divider, Tooltip } from '@mui/material'
import { partition } from 'lodash'
import { useHistory, Link } from 'react-router-dom'
import { useDeepCompareEffect } from 'react-use'
import { FeatureDict, selectFeatureIncludes, selectIsUserAdmin } from '@authed/selectors'
import { PopoverWithSearch } from '@components/popover-with-search'
import { useSearchParamsContext } from '@contexts/searchParamsContext'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { transformDatasetToById } from '@global/api/transforms'
import { useCreativeClassifications } from '@global/hooks/queries/useCreativeClassifications'
import { useDatasetsDataCache } from '@global/hooks/queries/useDatasetsDataCache'
import { useDatasetsDataQueryObserver } from '@global/hooks/queries/useDatasetsDataQueryObserver'
import { useTemplate } from '@global/hooks/useTemplate'
import { useAppSelector } from '@redux/hooks'
import { ApplyButton, ColumnList } from './components'
import { EnableAiClassifications } from './components/enable-ai-classifications'
import { reducer } from './reducer'
import * as css from './styles'
import { ActionType, Field, State, FieldTuple, ColumnCounts } from './types'
import {
  addDefaultHidden,
  getColCounts,
  getDefaultSelected,
  getFieldParam,
  isSelected,
  updateVisibleToTrue,
} from './utils'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'

const ROW_PROPERTY_KEY = 'rowPropertyName'

const initialState: State = {
  fieldsExpanded: true,
  propertiesExpanded: true,
  popoverOpen: false,
  allColumns: [],
  rowProperties: [],
  searchResults: [],
  searchTerm: '',
  selectAllFields: false,
  selectAllProperties: false,
  selected: {},
  creativeClassificationsExpanded: false,
}

interface Props {
  datasetId: number
  isSubmissionPage: boolean
  templateId: number
}

export const ToggleColumns = ({ datasetId, isSubmissionPage, templateId }: Props) => {
  const { params, updateParams, resetCache } = useSearchParamsContext()
  // Pendo event tracking logic
  const [resetVisibility, setResetVisibility] = useState<boolean>(false)
  const [noChange, setNoChange] = useState<boolean>(true)
  const [beforeApplyColCounts, setBeforeApplyColCounts] = useState<ColumnCounts>({
    defaultHiddenCount: 0,
    rowPropertiesCount: 0,
  })
  // end Pendo event tracking logic

  const [CREATIVE_CLASSIFICATION_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.CREATIVE_CLASSIFICATION]),
  )

  const history = useHistory()
  const { datasetLoading } = useDatasetQueryContext()
  const { data: classifications = [] } = useCreativeClassifications()
  const { data: dataset } = useDatasetsDataQueryObserver({ datasetId })
  const { addDatasetDataById } = useDatasetsDataCache()
  const allColumns: Field[] = dataset ? (dataset.allColumns as Field[]) : [] // TODO: fix allColumns typing
  const filteredColumns: Field[] | null = dataset ? dataset.filteredColumns : null
  const anchorEl = useRef<HTMLButtonElement | null>(null)
  const isAdmin = useAppSelector(selectIsUserAdmin)
  const { template } = useTemplate({ templateId: templateId.toString() })

  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    fieldsExpanded,
    creativeClassificationsExpanded,
    propertiesExpanded,
    popoverOpen,
    searchResults,
    searchTerm,
    selected,
  } = state

  const getInitialVisibleColumnsIds = () => {
    const urlParams = new URLSearchParams(params as Record<string, string>)
    const fieldIdsFromUrl = urlParams.getAll('fieldId')[0]?.split(',') ?? []
    const propertiesFromUrl = urlParams.getAll('property')[0]?.split(',') ?? []
    const paramValues = [...fieldIdsFromUrl, ...propertiesFromUrl]

    return paramValues
  }

  /**
   * This effect is needed because we are updating the cache with the initial visible columns.
   * Removing it may case the error "Cannot update a component (DatasetData) while rendering a
   * different component (ToggleColumns)". This is meant to run only once when the component mounts.
   */
  useDeepCompareEffect(() => {
    if (!filteredColumns && allColumns.length > 0) {
      const initialVisibleColumnsIds = getInitialVisibleColumnsIds()
      const initialSelected = getDefaultSelected(allColumns, initialVisibleColumnsIds)
      const properties = allColumns.filter(col => Boolean(col.rowPropertyName))

      dispatch({
        type: ActionType.updateInitialValues,
        payload: {
          allColumns,
          rowProperties: properties,
          selected: initialSelected,
        },
      })

      // Pendo event tracking logic
      const { defaultHiddenCount, rowPropertiesCount } = getColCounts(allColumns, filteredColumns)
      setBeforeApplyColCounts({
        defaultHiddenCount,
        rowPropertiesCount,
      })
      // end Pendo event tracking logic

      const initialVisibleColumns = allColumns
        .filter(field => isSelected(field, initialSelected))
        .map(addDefaultHidden)
        .map(updateVisibleToTrue)

      const transformedDataset = transformDatasetToById({
        ...dataset,
        id: String(datasetId),
        defaultFilteredColumns: initialVisibleColumns,
        showHidden: true,
        creativeConfiguration: template?.creativeConfiguration,
        classifications,
        rows: dataset?.rows ?? [],
        columns: dataset?.columns ?? [],
      })

      addDatasetDataById(transformedDataset)
    }
  }, [filteredColumns, allColumns])

  const [rowPropertyDisplayedFields, regularDisplayedFields]: FieldTuple = partition(
    allColumns.filter(({ isClassificationColumn }) => !isClassificationColumn),
    ROW_PROPERTY_KEY,
  )
  const [rowPropertySearchResults, regularSearchResults]: FieldTuple = partition(
    searchResults,
    ROW_PROPERTY_KEY,
  )
  const noSearchResults = Boolean(searchTerm && searchResults.length === 0)
  const showRegularFields = !searchTerm || regularSearchResults.length > 0
  const showRowProperties =
    !isSubmissionPage && (!searchTerm || rowPropertySearchResults.length > 0)
  const maxListHeight = '20vh'
  const noColumnsSelected = !Object.values(selected).find(Boolean)
  const applyIsDisabled = (searchTerm && searchResults.length === 0) || noColumnsSelected

  const aiCreativeClassificationsFields = allColumns.filter(
    ({ isClassificationColumn }) => isClassificationColumn,
  )

  return (
    <>
      <Button
        size="small"
        disableRipple
        ref={anchorEl}
        onClick={handleOpenPopover}
        disabled={datasetLoading}
        data-testid="columns-button"
      >
        <Visibility css={css.buttonIcon} />
        <css.ButtonLabel>{t`Columns`}</css.ButtonLabel>
      </Button>

      <PopoverWithSearch
        open={popoverOpen}
        anchorEl={anchorEl.current}
        handleOnClose={handleClosePopover}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        handleClearSearch={handleClearSearch}
        noSearchResults={noSearchResults}
        headerSlot={
          CREATIVE_CLASSIFICATION_ENABLED &&
          isAdmin && (
            <>
              <Link
                style={{ padding: 12, display: 'block' }}
                to={`/admin/templates/edit-template/${String(templateId)}/govern`}
              >
                <EnableAiClassifications />
              </Link>
              <Divider />
            </>
          )
        }
        contentStyle={{ minWidth: '320px', maxWidth: '320px', maxHeight: '72vh' }}
      >
        <css.ListContainer>
          {CREATIVE_CLASSIFICATION_ENABLED && aiCreativeClassificationsFields.length > 0 && (
            <>
              <Divider />
              <ColumnList
                title={t`AI creative classifications`}
                fields={aiCreativeClassificationsFields}
                expanded={creativeClassificationsExpanded || regularSearchResults.length > 0}
                searchResults={regularSearchResults}
                searchTerm={searchTerm}
                selected={selected}
                maxListHeight={maxListHeight}
                handleExpand={handleExpandCreativeClassifications}
                handleSelectAll={handleSelectCreativeClassifications}
                dispatch={dispatch}
                setNoChange={() => {
                  setNoChange(false)
                }}
              />
            </>
          )}
          {showRegularFields && (
            <>
              <Divider />
              <ColumnList
                title={t`Fields`}
                fields={regularDisplayedFields}
                expanded={fieldsExpanded || regularSearchResults.length > 0}
                searchResults={regularSearchResults}
                searchTerm={searchTerm}
                selected={selected}
                maxListHeight={maxListHeight}
                handleExpand={handleExpandFields}
                handleSelectAll={handleSelectFields}
                dispatch={dispatch}
                setNoChange={() => {
                  setNoChange(false)
                }}
              />
            </>
          )}
          {showRowProperties && (
            <>
              <Divider />
              <ColumnList
                title={t`Properties`}
                fields={rowPropertyDisplayedFields}
                expanded={propertiesExpanded || rowPropertySearchResults.length > 0}
                searchResults={rowPropertySearchResults}
                searchTerm={searchTerm}
                selected={selected}
                maxListHeight={maxListHeight}
                handleExpand={handleExpandProperties}
                handleSelectAll={handleSelectProperties}
                dispatch={dispatch}
                setNoChange={() => {
                  setNoChange(false)
                }}
              />
            </>
          )}
        </css.ListContainer>
        <Divider />
        <css.ButtonsContainer>
          <Button name="reset" color="primary" onClick={handleReset} data-testid="reset-button">
            {t`Reset`}
          </Button>
          <Button
            name="cancel"
            color="primary"
            variant="outlined"
            css={css.button}
            onClick={handleClosePopover}
            data-testid="cancel-button"
          >
            {t`Cancel`}
          </Button>
          {noColumnsSelected ? (
            <Tooltip
              title={t`You must select at least one column`}
              placement="top"
              arrow
              data-testid="apply-button-wrapper"
            >
              <span>
                <ApplyButton
                  disabled={applyIsDisabled}
                  onClick={handleApply}
                  data-testid="apply-button"
                />
              </span>
            </Tooltip>
          ) : (
            <ApplyButton
              disabled={applyIsDisabled}
              onClick={handleApply}
              data-testid="apply-button"
            />
          )}
        </css.ButtonsContainer>
      </PopoverWithSearch>
    </>
  )

  function handleOpenPopover(): void {
    dispatch({ type: ActionType.setPopoverOpen, payload: true })
  }

  function handleClosePopover(): void {
    dispatch({
      type: ActionType.resetToInitialState,
    })
  }

  function handleReset(): void {
    const visibleFields = allColumns.filter(({ visible }) => visible)

    dispatch({
      type: ActionType.resetToDefault,
      payload: visibleFields,
    })

    // Pendo event tracking logic
    setResetVisibility(true)
    setNoChange(false)
  }

  function handleExpandFields(): void {
    dispatch({ type: ActionType.setFieldsExpanded })
  }

  function handleExpandCreativeClassifications(): void {
    dispatch({ type: ActionType.setCreativeClassificationsExpanded })
  }

  function handleExpandProperties(): void {
    dispatch({ type: ActionType.setPropertiesExpanded })
  }

  function handleSearch(e: ChangeEvent<HTMLInputElement>): void {
    const { value } = e.target

    dispatch({
      type: ActionType.setSearchResults,
      payload: { searchTerm: value },
    })
  }

  function handleClearSearch(): void {
    dispatch({
      type: ActionType.setSearchResults,
      payload: { searchTerm: '' },
    })
  }

  function handleSelectFields(): void {
    dispatch({
      type: ActionType.selectFields,
      payload: regularDisplayedFields,
    })

    // Pendo event tracking logic
    setNoChange(false)
  }

  function handleSelectCreativeClassifications(): void {
    dispatch({
      type: ActionType.selectFields,
      payload: aiCreativeClassificationsFields,
    })

    // Pendo event tracking logic
    setNoChange(false)
  }

  function handleSelectProperties(): void {
    dispatch({
      type: ActionType.selectProperties,
      payload: rowPropertyDisplayedFields,
    })

    // Pendo event tracking logic
    setNoChange(false)
  }

  function handleApply(): void {
    const newVisibleColumns = allColumns
      .filter(field => isSelected(field, selected))
      .map(addDefaultHidden)
      .map(updateVisibleToTrue)

    const paramValues = newVisibleColumns.map(getFieldParam)
    const [properties, fieldIds] = partition(paramValues, value => Number.isNaN(value))

    if (resetVisibility) {
      updateParams({
        property: undefined,
        fieldId: undefined,
      })
    } else {
      updateParams({
        property: properties,
        fieldId: fieldIds,
      })
    }

    resetCache()

    const transformation = transformDatasetToById({
      ...dataset,
      id: String(datasetId),
      defaultFilteredColumns: newVisibleColumns,
      showHidden: true,
      creativeConfiguration: template?.creativeConfiguration,
      classifications,
      rows: dataset?.rows ?? [],
      columns: dataset?.columns ?? [],
    })

    addDatasetDataById(transformation)

    if (!noChange) {
      const { defaultHiddenCount, rowPropertiesCount } = getColCounts(allColumns, newVisibleColumns)
      const eventName =
        defaultHiddenCount > beforeApplyColCounts.defaultHiddenCount
          ? EventTrackingEventType.COLUMNS_HIDDEN
          : EventTrackingEventType.COLUMNS_SHOWN
      const isTemplateDetailsLocation = history.location.pathname.includes('template')

      trackEvent(eventName, {
        datasetType: isTemplateDetailsLocation ? 'Template' : 'Submission',
        netChange: defaultHiddenCount - beforeApplyColCounts.defaultHiddenCount,
        rowPropertiesCount: rowPropertiesCount - beforeApplyColCounts.rowPropertiesCount,
        resetVisibility,
        defaultHiddenCount,
      })
    }

    handleClosePopover()
  }
}
